import { Box, Paper, CircularProgress, Alert } from '@mui/material';
import { useRpcClientCall } from '@/providers/backendClient';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactJson from 'react-json-view';

interface ReportsViewerProps {
  filePath: string;
  currentPath: string;
}

/**
 * Resolves a relative markdown link to an absolute path
 * @param linkPath - The link path from the markdown (e.g., "./01-problem.md", "../README.md", "01-problem.md")
 * @param currentFilePath - The current file's path (e.g., "2025-12-05-1-ExpiredAuth0TokensConnectivityIssue/README.md")
 * @returns The resolved absolute path or null if it's not a markdown link
 */
const resolveMarkdownLink = (
  linkPath: string,
  currentFilePath: string,
): string | null => {
  // Only handle .md and .markdown files
  if (!linkPath.match(/\.(md|markdown)$/i)) {
    return null;
  }

  // Remove query strings and anchors
  const cleanPath = linkPath.split('?')[0].split('#')[0];

  // Get the directory of the current file
  const currentDir = currentFilePath.includes('/')
    ? currentFilePath.substring(0, currentFilePath.lastIndexOf('/'))
    : '';

  // Handle different link formats
  let resolvedPath: string;

  if (cleanPath.startsWith('./')) {
    // Relative to current directory: ./01-problem.md
    resolvedPath = currentDir
      ? `${currentDir}/${cleanPath.slice(2)}`
      : cleanPath.slice(2);
  } else if (cleanPath.startsWith('../')) {
    // Parent directory: ../README.md
    const parts = currentDir.split('/');
    let relativePath = cleanPath;
    while (relativePath.startsWith('../')) {
      parts.pop();
      relativePath = relativePath.slice(3);
    }
    resolvedPath =
      parts.length > 0
        ? `${parts.join('/')}/${relativePath}`
        : relativePath;
  } else if (cleanPath.startsWith('/')) {
    // Absolute from root: /2025-12-05-1-ExpiredAuth0TokensConnectivityIssue/README.md
    resolvedPath = cleanPath.slice(1);
  } else {
    // Relative to current directory: 01-problem.md
    resolvedPath = currentDir
      ? `${currentDir}/${cleanPath}`
      : cleanPath;
  }

  return resolvedPath;
};

/**
 * Detects the file type based on the file extension
 */
const getFileType = (filePath: string): 'markdown' | 'json' | 'code' | 'text' => {
  const extension = filePath.split('.').pop()?.toLowerCase() || '';
  
  if (['md', 'markdown'].includes(extension)) {
    return 'markdown';
  }
  
  if (extension === 'json') {
    return 'json';
  }
  
  // Code file extensions
  if (['ts', 'tsx', 'js', 'jsx', 'py', 'java', 'cpp', 'c', 'cs', 'go', 'rs', 'php', 'rb', 'swift', 'kt', 'scala', 'sh', 'bash', 'yaml', 'yml', 'xml', 'html', 'css', 'scss', 'less', 'sql', 'graphql', 'vue', 'svelte'].includes(extension)) {
    return 'code';
  }
  
  // Text file extensions (raw text files like logs, git output, etc.)
  // Files without extensions or with unknown extensions will also default to 'text'
  if (['txt', 'log', 'out', 'err', 'diff', 'patch'].includes(extension)) {
    return 'text';
  }
  
  // Default to text for files without recognized extensions
  return 'text';
};

/**
 * Gets the language name for syntax highlighting based on file extension
 */
const getLanguage = (filePath: string): string => {
  const extension = filePath.split('.').pop()?.toLowerCase() || '';
  
  const languageMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'tsx',
    js: 'javascript',
    jsx: 'jsx',
    py: 'python',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    cs: 'csharp',
    go: 'go',
    rs: 'rust',
    php: 'php',
    rb: 'ruby',
    swift: 'swift',
    kt: 'kotlin',
    scala: 'scala',
    sh: 'bash',
    bash: 'bash',
    yaml: 'yaml',
    yml: 'yaml',
    xml: 'xml',
    html: 'html',
    css: 'css',
    scss: 'scss',
    less: 'less',
    sql: 'sql',
    graphql: 'graphql',
    vue: 'vue',
    svelte: 'svelte',
  };
  
  return languageMap[extension] || 'text';
};

export const ReportsViewer = ({
  filePath,
  currentPath,
}: ReportsViewerProps) => {
  const { data, isLoading, error } = useRpcClientCall(
    'PUBLIC_ADMIN_reports_get',
    [filePath],
    {},
  );

  // Custom link component that generates properly formatted links
  const components: Components = {
    a: ({ href, children, ...props }) => {
      if (!href) {
        return <a {...props}>{children}</a>;
      }

      // Check if it's a markdown file link
      const isMarkdownLink = href.match(/\.(md|markdown)$/i);
      if (!isMarkdownLink) {
        // Not a markdown link, use regular anchor tag
        return (
          <a href={href} {...props} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      }

      // Resolve relative links to absolute paths
      let resolvedPath: string | null = null;

      // Check if it's already a reports path
      if (href.startsWith('/reports/view/')) {
        resolvedPath = decodeURIComponent(href.slice('/reports/view/'.length));
      } else if (href.startsWith('/reports/')) {
        resolvedPath = decodeURIComponent(href.slice('/reports/'.length));
      } else if (href.startsWith('#/reports/view/')) {
        resolvedPath = decodeURIComponent(href.slice('#/reports/view/'.length));
      } else if (href.startsWith('#/reports/')) {
        resolvedPath = decodeURIComponent(href.slice('#/reports/'.length));
      } else if (href.startsWith('http://') || href.startsWith('https://')) {
        // External URL - check if it's our origin
        try {
          const url = new URL(href);
          if (url.origin === window.location.origin) {
            const pathname = url.pathname;
            if (pathname.match(/\.(md|markdown)$/i) || (pathname.includes('/') && !pathname.startsWith('/reports/'))) {
              resolvedPath = pathname.startsWith('/') ? pathname.slice(1) : pathname;
            } else if (url.hash && url.hash.startsWith('#/reports/')) {
              resolvedPath = decodeURIComponent(url.hash.slice('#/reports/'.length));
            }
          }
        } catch {
          // Invalid URL, treat as external
        }
      } else {
        // Relative link - resolve it
        resolvedPath = resolveMarkdownLink(href, currentPath);
      }

      if (resolvedPath) {
        // Generate properly formatted link for dialog view
        // Check if we're in the dialog view (URL contains /reports/view/)
        const currentHash = window.location.hash;
        const isInDialogView = currentHash.includes('/reports/view/');
        const linkPath = isInDialogView
          ? `/reports/view/${encodeURIComponent(resolvedPath)}`
          : `/reports/${encodeURIComponent(resolvedPath)}`;
        
        return (
          <Link to={linkPath} {...props}>
            {children}
          </Link>
        );
      }

      // Fallback to regular anchor tag
      return (
        <a href={href} {...props} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load report:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  const dataResult = data as
    | { success: boolean; content?: string; error?: string }
    | undefined;

  if (!dataResult?.success || !dataResult?.content) {
    return (
      <Alert severity="warning">
        {dataResult?.error || 'Report not found'}
      </Alert>
    );
  }

  const fileType = getFileType(filePath);

  // Render JSON files with react-json-view
  if (fileType === 'json') {
    try {
      const jsonData = JSON.parse(dataResult.content);
      return (
        <Paper
          sx={{
            p: 0,
            height: '100%',
            overflow: 'auto',
            backgroundColor: '#1e1e1e',
          }}
        >
          <Box sx={{ p: 2 }}>
            <ReactJson
              src={jsonData}
              theme="monokai"
              displayDataTypes={false}
              enableClipboard={true}
              collapsed={2}
              style={{
                backgroundColor: 'transparent',
                fontFamily: 'monospace',
              }}
            />
          </Box>
        </Paper>
      );
    } catch {
      // If JSON parsing fails, fall through to code viewer
    }
  }

  // Render code files with syntax highlighting
  if (fileType === 'code' || fileType === 'text') {
    const language = getLanguage(filePath);
    return (
      <Paper
        sx={{
          p: 0,
          height: '100%',
          overflow: 'auto',
          backgroundColor: '#1e1e1e',
        }}
      >
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: '16px',
            backgroundColor: '#1e1e1e',
            fontSize: '14px',
          }}
          showLineNumbers
          wrapLines
        >
          {dataResult.content}
        </SyntaxHighlighter>
      </Paper>
    );
  }

  // Render markdown files with ReactMarkdown
  return (
    <Paper
      sx={{
        p: 0,
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'rgb(13, 17, 23)',
      }}
    >
      <Box
        sx={{
          padding: '32px',
          backgroundColor: 'rgb(13, 17, 23)',
          color: '#c9d1d9',
          '& > *': {
            marginBottom: '16px',
          },
          '& > *:last-child': {
            marginBottom: 0,
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '24px',
            marginBottom: '16px',
            color: '#c9d1d9',
            fontWeight: 600,
          },
          '& h1': {
            fontSize: '2em',
            borderBottom: '1px solid #30363d',
            paddingBottom: '0.3em',
          },
          '& h2': {
            fontSize: '1.5em',
            borderBottom: '1px solid #30363d',
            paddingBottom: '0.3em',
          },
          '& p': {
            marginBottom: '16px',
            lineHeight: 1.6,
          },
          '& ul, & ol': {
            marginBottom: '16px',
            paddingLeft: '24px',
          },
          '& li': {
            marginBottom: '8px',
          },
          '& a': {
            color: '#58a6ff',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& code': {
            backgroundColor: 'rgba(110, 118, 129, 0.4)',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontSize: '85%',
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          },
          '& pre': {
            backgroundColor: '#161b22',
            padding: '16px',
            borderRadius: '6px',
            overflow: 'auto',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
          },
          '& blockquote': {
            padding: '0 1em',
            color: '#8b949e',
            borderLeft: '0.25em solid #30363d',
            margin: '16px 0',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: '16px',
          },
          '& th, & td': {
            border: '1px solid #30363d',
            padding: '6px 13px',
          },
          '& th': {
            backgroundColor: '#161b22',
            fontWeight: 600,
          },
          '& tr:nth-of-type(2n)': {
            backgroundColor: '#161b22',
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize]]}
          components={components}
        >
          {dataResult.content}
        </ReactMarkdown>
      </Box>
    </Paper>
  );
};
